import type { KnittingEvent, RowTiming } from '../types/session';

/**
 * Walk a session's verbose history and emit per-row active-time totals.
 *
 * Mirrors engine.ir.ts's PREV_ROW preprocessing (each PREV_ROW splices out
 * the most recent NEXT_ROW). Active time accumulates only between START/RESUME
 * and the next PAUSE/NEXT_ROW boundary, so PAUSE windows are excluded.
 */
export function archiveHistory(history: KnittingEvent[]): {
  rowTimings: RowTiming[];
  totalTimeMs: number;
  currentRow: number;
} {
  const effective: KnittingEvent[] = [];
  for (const e of history) {
    if (e.action === 'PREV_ROW') {
      for (let i = effective.length - 1; i >= 0; i--) {
        if (effective[i].action === 'NEXT_ROW') {
          effective.splice(i, 1);
          break;
        }
      }
    } else {
      effective.push(e);
    }
  }

  const rowMs: number[] = [];
  let currentRow = 0;
  let activeStartMs: number | null = null;

  const charge = (ts: number) => {
    if (activeStartMs == null) return;
    const delta = Math.max(0, ts - activeStartMs);
    rowMs[currentRow] = (rowMs[currentRow] ?? 0) + delta;
  };

  for (const e of effective) {
    const ts = Date.parse(e.timestamp);
    switch (e.action) {
      case 'START':
      case 'RESUME':
        activeStartMs = ts;
        break;
      case 'PAUSE':
        charge(ts);
        activeStartMs = null;
        break;
      case 'NEXT_ROW':
        charge(ts);
        if (activeStartMs != null) activeStartMs = ts;
        currentRow++;
        break;
      case 'EXPAND_NODE':
        // Doesn't affect timing; fall through.
        break;
    }
  }

  const rowTimings: RowTiming[] = [];
  let totalTimeMs = 0;
  for (let i = 0; i < rowMs.length; i++) {
    const ms = rowMs[i] ?? 0;
    if (ms > 0) rowTimings.push([i, ms]);
    totalTimeMs += ms;
  }

  return { rowTimings, totalTimeMs, currentRow };
}
